import * as React from 'react';
import { ImageOff } from 'lucide-react';
import { urlImagemProduto } from '@wms/shared';
import { cn } from '@/lib/utils';

/**
 * Miniatura do produto.
 *
 * Nem todo SKU tem imagem e NÃO existe endpoint que diga quais têm (o 32991,
 * por exemplo, dá 404). A estratégia é otimista: pede a imagem e, no `onError`,
 * troca por um marcador neutro. Fazer HEAD antes dobraria as requisições para
 * descobrir o que o próprio <img> já descobre.
 */
export function ImagemProduto({
  sku,
  indice = 0,
  tamanho = 40,
  alt,
  className,
  arredondada = true,
}: {
  sku: number;
  indice?: number | undefined;
  tamanho?: number | undefined;
  alt?: string | undefined;
  className?: string | undefined;
  arredondada?: boolean | undefined;
}): JSX.Element {
  const [falhou, definirFalhou] = React.useState(false);

  // Trocar de SKU na mesma instância (linha de tabela virtualizada) precisa
  // reabilitar a tentativa, senão a miniatura some para o produto seguinte.
  React.useEffect(() => {
    definirFalhou(false);
  }, [sku, indice]);

  const estilo = { width: tamanho, height: tamanho } as const;

  if (falhou) {
    return (
      <span
        aria-hidden
        style={estilo}
        className={cn(
          'flex shrink-0 items-center justify-center border border-borda bg-superficie2 text-textoFraco',
          arredondada && 'rounded',
          className,
        )}
      >
        <ImageOff style={{ width: Math.min(18, tamanho * 0.5), height: Math.min(18, tamanho * 0.5) }} />
      </span>
    );
  }

  return (
    <img
      src={urlImagemProduto(sku, indice)}
      alt={alt ?? `Foto do produto ${sku}`}
      loading="lazy"
      decoding="async"
      style={estilo}
      onError={() => definirFalhou(true)}
      className={cn(
        'shrink-0 border border-borda bg-white object-contain',
        arredondada && 'rounded',
        className,
      )}
    />
  );
}
